import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, FormControlLabel, Radio, Stack } from '@mui/material';
import { useTheme, lighten, alpha } from '@mui/material/styles';
import type { ReactNode } from 'react';

import { BoxFlex } from '@woovi/ui';

type RadioCardOptionProps<T> = {
  children?: ReactNode;
  value: T;
  checked?: boolean;
  disabled?: boolean;
  handleClickRadio: (value: T) => void;
  'data-testid'?: string;
  label: ReactNode | string;
};

const RadioCardOption = <T,>({
  children,
  value,
  handleClickRadio,
  checked,
  label,
  disabled,
  ...props
}: RadioCardOptionProps<T>): ReactNode => {
  const theme = useTheme();

  const getHover = () => {
    if (theme.palette.mode === 'dark') {
      return {
        borderColor: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
      };
    }

    return {
      borderColor: theme.palette.primary.main,
      backgroundColor: lighten(theme.palette.primary.main, 0.9),
    };
  };

  return (
    <BoxFlex
      sx={{
        position: 'relative',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      {disabled && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            background: 'rgba(255, 255, 255, 0.5)',
            zIndex: 2,
          }}
        />
      )}
      <Box
        sx={{
          flex: 1,
          alignItems: 'center',
          display: 'flex',
          paddingX: 2,
          paddingY: 1,
          cursor: 'pointer',
          border: 2,
          borderColor: 'lightGray',
          borderRadius: theme.spacing(1),
          '&:hover': getHover(),
        }}
        onClick={() => {
          handleClickRadio(value);
        }}
      >
        <Stack>
          <BoxFlex sx={{ justifyContent: 'space-between' }}>
            <BoxFlex sx={{ alignItems: 'center' }}>
              <FormControlLabel
                value={value}
                control={
                  <Radio
                    checkedIcon={
                      <CheckCircleIcon
                        sx={{ color: theme.palette.primary.main }}
                      />
                    }
                  />
                }
                label={label}
                checked={checked}
                data-testid={props['data-testid']}
              />
            </BoxFlex>
          </BoxFlex>
          {children}
        </Stack>
      </Box>
    </BoxFlex>
  );
};

export default RadioCardOption;
